/**
 * DAS FREIE RASTER DES DASHBOARDS (Auftrag #35, SYS-12 i. d. F. AeA-40).
 *
 * Eine Quelle fuer API und Oberflaeche. Die API prueft und verdichtet damit,
 * was sie ablegt; die Oberflaeche rechnet damit waehrend des Ziehens die neue
 * Lage. Liefen die beiden auseinander, zeigte die Maske beim Loslassen eine
 * Lage, die der Server danach anders ablegt.
 *
 * OHNE EINFUHR, UND ZWAR ABSICHTLICH. Die Oberflaeche fuehrt diese Datei ueber
 * ihren Quellpfad ein (`@crm/kontrakte/src/raster`) und nicht ueber das
 * CommonJS-Paket, das zod mitzoege (siehe `index.ts`). Deshalb importiert sie
 * nichts — auch nichts aus diesem Paket.
 *
 * ============================================================================
 * DAS RASTER
 * ============================================================================
 *
 * Vier Spalten, Zeilen fester Hoehe. Jedes Feld hat eine Lage: Spalte x
 * (0–3), Zeile y (ab 0), Breite 1–4 Spalten, Hoehe 1–4 Zeilen. Kein feineres
 * Raster — der Auftraggeber wollte es „frei, aber nicht zu kleinteilig".
 *
 * LUECKEN SCHLIESSEN SICH NACH OBEN (`verdichte`), wie bei Grafana: Jedes
 * Feld rueckt so weit hinauf, wie ueber ihm frei ist. Damit gibt es keine
 * Luecke, ueber der nichts steht, und die Lesereihenfolge — oben links nach
 * unten rechts, `inLesefolge` — ist eindeutig (WCAG 1.3.2, 2.4.3).
 *
 * VERSCHIEBEN UND BEMESSEN (`lege`): Das bewegte Feld nimmt seine neue Lage
 * ein, die anderen weichen aus — trifft es genau eines, das an seinen alten
 * Platz passt, tauschen die beiden; sonst rutscht, wer im Weg ist, darunter,
 * und wer ueber dem freien Platz steht, rueckt nach. Danach wird verdichtet; auch das
 * bewegte Feld steigt, wenn ueber ihm nichts steht.
 */

export const RASTERSPALTEN = 4;
/** Genug fuer 64 Felder zu je 4 Zeilen; die Tabelle prueft dieselbe Grenze (0288). */
export const RASTERZEILEN = 256;
export const HOECHSTENS_FELDER = 64;

export type Groesse = { readonly breite: number; readonly hoehe: number };
export type Lage = Groesse & { readonly x: number; readonly y: number };
export type Rasterfeld = Lage & { readonly kennung: string };

type Art = 'aufgaben' | 'verlauf' | 'bericht' | 'kachel';

function artVon(kennung: string): Art {
  if (kennung === 'aufgaben') return 'aufgaben';
  if (kennung === 'verlauf') return 'verlauf';
  if (kennung.startsWith('bericht:')) return 'bericht';
  return 'kachel';
}

/**
 * Mindestgroessen (Auftrag #35): Kachel 1×1, Verlauf 1×2, „Das steht an" 2×2,
 * Bericht 2×2. Kleiner liest man sie nicht mehr.
 */
const MINDEST: Readonly<Record<Art, Groesse>> = {
  kachel: { breite: 1, hoehe: 1 },
  verlauf: { breite: 1, hoehe: 2 },
  aufgaben: { breite: 2, hoehe: 2 },
  bericht: { breite: 2, hoehe: 2 },
};

/** Vorgaben (Auftrag #35): Kachel 1×1, alles andere 2×2. */
const VORGABE: Readonly<Record<Art, Groesse>> = {
  kachel: { breite: 1, hoehe: 1 },
  verlauf: { breite: 2, hoehe: 2 },
  aufgaben: { breite: 2, hoehe: 2 },
  bericht: { breite: 2, hoehe: 2 },
};

export function mindestgroesse(kennung: string): Groesse {
  return MINDEST[artVon(kennung)];
}

export function vorgabegroesse(kennung: string): Groesse {
  return VORGABE[artVon(kennung)];
}

export function ueberlappen(a: Lage, b: Lage): boolean {
  return a.x < b.x + b.breite && b.x < a.x + a.breite && a.y < b.y + b.hoehe && b.y < a.y + a.hoehe;
}

function trifft(lage: Lage, gelegt: readonly Lage[]): boolean {
  return gelegt.some((g) => ueberlappen(lage, g));
}

/** Oben links nach unten rechts: nach Zeile, dann Spalte. */
export function inLesefolge<F extends Lage>(felder: readonly F[]): F[] {
  return [...felder].sort((a, b) => a.y - b.y || a.x - b.x);
}

/**
 * Luecken schliessen sich nach oben. Mit `fest` behaelt dieses eine Feld seine
 * Lage und die anderen weichen ihm aus — so entsteht beim Ziehen die neue
 * Lage; ohne `fest` rueckt alles, auch es selbst.
 */
export function verdichte<F extends Rasterfeld>(felder: readonly F[], fest?: string): F[] {
  const bleibt = fest === undefined ? undefined : felder.find((f) => f.kennung === fest);
  const gelegt: F[] = bleibt === undefined ? [] : [bleibt];
  for (const f of inLesefolge(felder.filter((f) => f !== bleibt))) {
    let y = f.y;
    while (y > 0 && !trifft({ ...f, y: y - 1 }, gelegt)) y -= 1;
    while (trifft({ ...f, y }, gelegt)) y += 1;
    gelegt.push({ ...f, y });
  }
  return inLesefolge(gelegt);
}

/**
 * Weitere Felder zu schon gelegten, ohne die gelegten zu bewegen: Jedes
 * weitere behaelt seine Spalte und rutscht nur so weit nach unten, bis es
 * frei liegt. So kommen ruhende Widgets (ohne Recht, SYS-12) an ihren Platz
 * zurueck, ohne das Raster des Benutzers umzustellen.
 */
export function ergaenze<F extends Rasterfeld>(gelegt: readonly F[], weitere: readonly F[]): F[] {
  const alle = [...gelegt];
  for (const f of inLesefolge(weitere)) {
    let y = f.y;
    while (trifft({ ...f, y }, alle)) y += 1;
    alle.push({ ...f, y });
  }
  return inLesefolge(alle);
}

/**
 * Ein Feld an eine neue Lage oder in eine neue Groesse; die anderen weichen
 * aus, dann wird verdichtet. Die Lage wird ins Raster geklemmt, die Groesse
 * auf die Mindestgroesse der Art.
 */
export function lege<F extends Rasterfeld>(
  felder: readonly F[],
  kennung: string,
  neu: Partial<Lage>,
): F[] {
  const alt = felder.find((f) => f.kennung === kennung);
  if (alt === undefined) return verdichte(felder);
  const min = mindestgroesse(kennung);
  const breite = klemme(neu.breite ?? alt.breite, min.breite, RASTERSPALTEN);
  const hoehe = klemme(neu.hoehe ?? alt.hoehe, min.hoehe, 4);
  const x = klemme(neu.x ?? alt.x, 0, RASTERSPALTEN - breite);
  const y = klemme(neu.y ?? alt.y, 0, RASTERZEILEN - hoehe);
  const ziel = { ...alt, x, y, breite, hoehe };
  let belegt = felder.map((f) => (f === alt ? ziel : f));
  /*
   * TAUSCHEN, WO ES GEHT. Trifft das Feld an seiner neuen Stelle genau eines
   * und passt dieses an den alten Platz, ohne etwas zu treffen, tauschen die
   * beiden — „nach links" neben eine Kachel heisst: sie wechseln die Plaetze,
   * und nicht: die Kachel rutscht eine Zeile tiefer.
   */
  const getroffen = belegt.filter((f) => f !== ziel && ueberlappen(f, ziel));
  if (getroffen.length === 1) {
    const anderes = getroffen[0]!;
    const getauscht = { ...anderes, x: Math.min(alt.x, RASTERSPALTEN - anderes.breite), y: alt.y };
    if (!trifft(getauscht, belegt.filter((f) => f !== anderes))) {
      belegt = belegt.map((f) => (f === anderes ? getauscht : f));
    }
  }
  return verdichte(verdichte(belegt, kennung));
}

/** Gleiche Lage aller Felder? */
export function gleicheLage(a: readonly Rasterfeld[], b: readonly Rasterfeld[]): boolean {
  if (a.length !== b.length) return false;
  return a.every((f) => {
    const g = b.find((h) => h.kennung === f.kennung);
    return g !== undefined && g.x === f.x && g.y === f.y && g.breite === f.breite && g.hoehe === f.hoehe;
  });
}

/**
 * Einen Schritt in eine Richtung — fuer das Menue ohne Ziehen (WCAG 2.5.7).
 *
 * Ein Schritt ist so weit, wie noetig, damit sich etwas aendert: „nach unten"
 * unter ein Feld, das zwei Zeilen hoch ist, braucht zwei Zeilen, sonst rueckte
 * das Feld beim Verdichten gleich wieder hinauf. `null`: In diese Richtung
 * geht es nicht weiter.
 */
export function schritt<F extends Rasterfeld>(
  felder: readonly F[],
  kennung: string,
  richtung: 'links' | 'rechts' | 'oben' | 'unten',
): F[] | null {
  const alt = felder.find((f) => f.kennung === kennung);
  if (alt === undefined) return null;
  for (let weite = 1; weite <= 4; weite += 1) {
    const neu =
      richtung === 'links'
        ? { x: alt.x - weite }
        : richtung === 'rechts'
          ? { x: alt.x + weite }
          : richtung === 'oben'
            ? { y: alt.y - weite }
            : { y: alt.y + weite };
    if ((neu.x !== undefined && (neu.x < 0 || neu.x + alt.breite > RASTERSPALTEN)) || (neu.y !== undefined && neu.y < 0)) {
      return null;
    }
    const ergebnis = lege(felder, kennung, neu);
    if (!gleicheLage(ergebnis, felder)) return ergebnis;
  }
  return null;
}

/** Die erste freie Stelle in Lesefolge, an der ein Feld dieser Groesse Platz hat. */
export function ersteFreieStelle(felder: readonly Lage[], groesse: Groesse): { x: number; y: number } {
  for (let y = 0; y <= RASTERZEILEN - groesse.hoehe; y += 1) {
    for (let x = 0; x <= RASTERSPALTEN - groesse.breite; x += 1) {
      if (!trifft({ x, y, ...groesse }, felder)) return { x, y };
    }
  }
  return { x: 0, y: RASTERZEILEN - groesse.hoehe };
}

/**
 * DIE FREIEN FLÄCHEN ZUM EINFÜGEN (Auftrag #37, SYS-12).
 *
 * Auftraggeber: „Idealerweise kann man im Bearbeitungsmodus die Flächen zum
 * Hinzufügen direkt auswählen." Angeboten wird jede freie Zelle, auf der ein
 * eingefügtes Widget auch LIEGEN BLEIBT: in der ersten Zeile oder direkt unter
 * einem Widget — bis eine Zeile unter das unterste. Eine Zelle, über der es
 * frei ist, böte eine Stelle an, von der das Widget beim Verdichten gleich
 * nach oben rutschte; „genau dort" wäre dann eine Behauptung.
 */
export function freieFlaechen(felder: readonly Lage[]): { x: number; y: number }[] {
  const unten = Math.min(
    felder.reduce((tiefste, f) => Math.max(tiefste, f.y + f.hoehe), 0),
    RASTERZEILEN - 1,
  );
  const flaechen: { x: number; y: number }[] = [];
  for (let y = 0; y <= unten; y += 1) {
    for (let x = 0; x < RASTERSPALTEN; x += 1) {
      if (trifft({ x, y, breite: 1, hoehe: 1 }, felder)) continue;
      if (y === 0 || trifft({ x, y: y - 1, breite: 1, hoehe: 1 }, felder)) flaechen.push({ x, y });
    }
  }
  return flaechen;
}

/**
 * Ein Widget GENAU an einer Stelle einsetzen (Auftrag #37): in seiner
 * Vorgabegröße, soweit dort Platz ist, sonst so groß, wie es passt — nie
 * kleiner als seine Mindestgröße. Bei gleicher Fläche gewinnt die Breite.
 * Passt nicht einmal die Mindestgröße, kommt es in ihr an die Stelle (so weit
 * links, dass es ins Raster passt), und die anderen weichen aus wie beim
 * Ziehen.
 */
export function setzeEin<F extends Rasterfeld>(
  felder: readonly F[],
  neu: { readonly kennung: string; readonly vorgabe: Groesse },
  stelle: { readonly x: number; readonly y: number },
): Rasterfeld[] {
  const min = mindestgroesse(neu.kennung);
  let beste: Groesse | undefined;
  for (let breite = neu.vorgabe.breite; breite >= min.breite; breite -= 1) {
    for (let hoehe = neu.vorgabe.hoehe; hoehe >= min.hoehe; hoehe -= 1) {
      if (stelle.x + breite > RASTERSPALTEN || stelle.y + hoehe > RASTERZEILEN) continue;
      if (trifft({ ...stelle, breite, hoehe }, felder)) continue;
      if (beste === undefined || breite * hoehe > beste.breite * beste.hoehe) beste = { breite, hoehe };
    }
  }
  if (beste !== undefined) return verdichte<Rasterfeld>([...felder, { kennung: neu.kennung, ...stelle, ...beste }]);
  const x = Math.min(stelle.x, RASTERSPALTEN - min.breite);
  return lege<Rasterfeld>([...felder, { kennung: neu.kennung, x, y: stelle.y, ...min }], neu.kennung, {});
}

/**
 * Eine Folge zeilenweise ins Raster legen — fuer die Vorgabe und fuer die
 * Uebernahme alter Anordnungen (0288, dieselbe Rechnung in SQL). Jedes Feld
 * kommt an die erste freie Stelle HINTER dem vorigen, damit die Folge die
 * Lesereihenfolge bleibt.
 */
export function fliesse<F extends { readonly kennung: string } & Groesse>(
  folge: readonly F[],
): (F & { x: number; y: number })[] {
  return fliesseAb(folge, [], { x: 0, y: 0 });
}

/**
 * Neue Widgets HINTER das letzte Feld legen, ohne die gelegten zu bewegen
 * (SYS-12 i. d. F. AeA-40, entschieden 07.10.2026: eine neu gebaute Kachel
 * kommt bei eigener Anordnung von selbst ans Ende). Zeilenweise wie
 * `fliesse`, beginnend rechts neben dem letzten Feld in Lesefolge; geliefert
 * werden nur die neuen Felder. Danach wird wie immer verdichtet.
 */
export function haengeAn<F extends { readonly kennung: string } & Groesse>(
  felder: readonly Rasterfeld[],
  neue: readonly F[],
): (F & { x: number; y: number })[] {
  const letztes = inLesefolge(felder).at(-1);
  return fliesseAb(neue, felder, letztes === undefined ? { x: 0, y: 0 } : { x: letztes.x + letztes.breite, y: letztes.y });
}

function fliesseAb<F extends { readonly kennung: string } & Groesse>(
  folge: readonly F[],
  belegt: readonly Lage[],
  ab: { readonly x: number; readonly y: number },
): (F & { x: number; y: number })[] {
  const gelegt: (F & { x: number; y: number })[] = [];
  let ay = ab.y;
  let ax = ab.x;
  for (const f of folge) {
    let platz: { x: number; y: number } | undefined;
    for (let y = ay; platz === undefined && y <= RASTERZEILEN - f.hoehe; y += 1) {
      for (let x = y === ay ? ax : 0; x <= RASTERSPALTEN - f.breite; x += 1) {
        const lage = { x, y, breite: f.breite, hoehe: f.hoehe };
        if (!trifft(lage, gelegt) && !trifft(lage, belegt)) {
          platz = { x, y };
          break;
        }
      }
    }
    const lage = platz ?? { x: 0, y: RASTERZEILEN - f.hoehe };
    gelegt.push({ ...f, ...lage });
    ay = lage.y;
    ax = lage.x;
  }
  return gelegt;
}

/**
 * Was an einer Folge von Feldern nicht stimmt, oder null (Auftrag #35). Die
 * API weist damit ab; die Tabelle prueft dasselbe (0288).
 */
export function rasterfehler(felder: readonly Rasterfeld[]): string | null {
  if (felder.length > HOECHSTENS_FELDER) return `Höchstens ${String(HOECHSTENS_FELDER)} Felder.`;
  const gesehen = new Set<string>();
  for (const f of felder) {
    if (gesehen.has(f.kennung)) return `„${f.kennung}" steht zweimal im Raster.`;
    gesehen.add(f.kennung);
    const ganz = [f.x, f.y, f.breite, f.hoehe].every((z) => Number.isInteger(z));
    if (!ganz || f.x < 0 || f.y < 0 || f.breite < 1 || f.breite > RASTERSPALTEN || f.hoehe < 1 || f.hoehe > 4) {
      return `„${f.kennung}" liegt nicht im Raster.`;
    }
    if (f.x + f.breite > RASTERSPALTEN || f.y + f.hoehe > RASTERZEILEN) {
      return `„${f.kennung}" ragt über das Raster hinaus.`;
    }
    const min = mindestgroesse(f.kennung);
    if (f.breite < min.breite || f.hoehe < min.hoehe) {
      return `„${f.kennung}" ist kleiner als ${String(min.breite)}×${String(min.hoehe)}.`;
    }
  }
  for (let i = 0; i < felder.length; i += 1) {
    for (let j = i + 1; j < felder.length; j += 1) {
      if (ueberlappen(felder[i]!, felder[j]!)) {
        return `„${felder[i]!.kennung}" und „${felder[j]!.kennung}" überlappen sich.`;
      }
    }
  }
  return null;
}

function klemme(wert: number, unten: number, oben: number): number {
  return Math.min(Math.max(Math.round(wert), unten), oben);
}
